import React from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid } from '@material-ui/core'
import * as Yup from 'yup'
import { Formik } from 'formik'

import { Theme } from '~/styles'
import { Heading, Typography, Button, Header, Main } from '~/components'
import { insurancePayerSchema } from '~/utils'
import Loader from '~/components/Loader/Loader'

export interface InsurancePayerOption {
  id: number
  name: string
  logo?: string | null
  fireflyAccepted: boolean
}

interface InsurancePayerFormState {
  insurancePayer: InsurancePayerOption | null
}

interface InsurancePayerSelectionUIProps {
  payers: InsurancePayerOption[]
  isLoading: boolean
  onSubmit: (payer: InsurancePayerOption | null) => void | Promise<void>
}

/**
 * Reusable UI component for insurance payer selection.
 * Used by both authenticated and unauthenticated flows.
 */
export const InsurancePayerSelectionUI: React.FC<InsurancePayerSelectionUIProps> = ({
  payers,
  isLoading,
  onSubmit,
}) => {
  const validationSchema = Yup.object().shape({
    insurancePayer: insurancePayerSchema,
  })

  const initialState: InsurancePayerFormState = {
    insurancePayer: null,
  }

  return isLoading ? (
    <>
      <Header />
      <Loader />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <Box mt={2} mb={2}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} md={6}>
                  <Main px={{ xs: 0 }}>
                    <Box mt={7} />
                    <Heading>Who is your health insurance provider?</Heading>
                    <Typography variant="subText">
                      We accept most plans for the following providers.
                    </Typography>
                    <Box mt={3} />
                    <Formik<InsurancePayerFormState>
                      validateOnMount={true}
                      validateOnChange={false}
                      initialValues={initialState}
                      onSubmit={values => {
                        onSubmit(values.insurancePayer)
                      }}
                      validationSchema={validationSchema}
                    >
                      {formik => {
                        return (
                          <form onSubmit={formik.handleSubmit} css={styles.wrapper}>
                            <>
                              <Box mb={3} css={styles.buttonGrid}>
                                {payers.map((option, i) => {
                                  return (
                                    <Button
                                      key={i}
                                      title={option.name}
                                      buttonColor="white"
                                      css={styles.button}
                                      disabled={isLoading}
                                      type="submit"
                                      onClick={() => {
                                        formik.setFieldValue('insurancePayer', option)
                                      }}
                                      sx={{
                                        position: 'relative',
                                        boxShadow: 'none',
                                        borderColor: 'black',
                                        '&:focus': {
                                          borderColor: 'red',
                                          boxShadow: 'none',
                                        },
                                      }}
                                    >
                                      {option.logo ? (
                                        <img src={option.logo} alt={option.name} />
                                      ) : (
                                        <Typography variant="primary" css={styles.logoText}>
                                          {option.name}
                                        </Typography>
                                      )}
                                    </Button>
                                  )
                                })}
                              </Box>
                              <Typography
                                variant="subText"
                                onClick={() => {
                                  onSubmit(null)
                                }}
                                tabIndex={0}
                                role="button"
                                css={styles.pressableText}
                              >
                                My insurance provider isn&apos;t on this list
                              </Typography>
                            </>
                          </form>
                        )
                      }}
                    </Formik>
                  </Main>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  wrapper: () => css({ width: '100%' }),
  buttonGrid: () =>
    css({
      display: 'flex',
      flexWrap: 'wrap',
      gap: '19px',
      flexDirection: 'column',
    }),
  button: (theme: Theme) =>
    css({
      flex: '0 0 calc(33.333% - 38px / 3)',
      padding: 0,
      borderRadius: '10px',
      borderWidth: '1px',
      borderColor: theme.colors.gray[400],
      borderStyle: 'solid',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      height: '84px',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      ...{
        '&:hover': {
          borderColor: theme.colors.navy[500],
          borderWidth: '2px',
          boxShadow: '0 4px 12px 0 rgba(7, 31, 62, 0.15)',
          transform: 'translateY(-2px)',
        },
      },
      [theme.mediaQueries.sm]: {
        flex: '0 0 calc(33.333% - 38px / 3)',
      },
    }),
  logoText: (theme: Theme) =>
    css({
      color: theme.colors.navy[500],
      fontSize: '16px',
      padding: '0 12px',
    }),
  pressableText: (theme: Theme) =>
    css({
      textDecorationLine: 'underline',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        color: theme.colors.navy[500],
        transform: 'scale(1.01)',
      },
    }),
}
